import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faClock, faSearch, faSpinner, faEye, faHeart } from '@fortawesome/free-solid-svg-icons';
import './Blog.css';

var API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

/* ── Animation variants ───────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
var stagger    = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
var cardStagger= { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/* ── Featured card (first post) ──────────────────────────────── */
function FeaturedPost({ post, index }) {
  var STATIC_BASE = API_BASE.replace('/api/v1', '');
  function imgSrc(p) {
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return STATIC_BASE + (p.startsWith('/') ? '' : '/') + p;
  }
  var cover = imgSrc(post.coverImage);

  return (
    <motion.article className="blog-featured" variants={fadeUp} role="article">
      {cover && (
        <div className="blog-featured__img-wrap">
          <img src={cover} alt={post.title} loading="eager" className="blog-featured__img" />
          <div className="blog-featured__img-overlay" />
        </div>
      )}
      <div className="blog-featured__body">
        <div className="blog-featured__meta">
          {post.category && (
            <span className="blog-featured__cat" style={{ '--cat-color': post.category.color || 'var(--clr-gold)' }}>
              {post.category.name}
            </span>
          )}
          <span className="blog-featured__badge">Featured</span>
        </div>
        <h2 className="blog-featured__title">
          <Link to={'/blog/' + post.slug}>{post.title}</Link>
        </h2>
        <p className="blog-featured__excerpt">{post.excerpt}</p>
        <div className="blog-featured__footer">
          <div className="blog-featured__byline">
            <span className="blog-featured__date">{formatDate(post.createdAt)}</span>
            <span className="blog-featured__dot" aria-hidden="true">·</span>
            <span className="blog-featured__read"><FontAwesomeIcon icon={faClock} /> {post.readTimeMinutes} min read</span>
          </div>
          <Link to={'/blog/' + post.slug} className="blog-featured__cta">
            Read Article <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

/* ── Standard post card ───────────────────────────────────────── */
function PostCard({ post, index }) {
  var STATIC_BASE = API_BASE.replace('/api/v1', '');
  function imgSrc(p) {
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return STATIC_BASE + (p.startsWith('/') ? '' : '/') + p;
  }
  var cover = imgSrc(post.coverImage);

  return (
    <motion.article className={'blog-card' + (cover ? ' blog-card--has-cover' : '')} variants={fadeUp} role="listitem">
      {cover && (
        <Link to={'/blog/' + post.slug} className="blog-card__cover" tabIndex={-1} aria-hidden="true">
          <img src={cover} alt="" loading="lazy" />
          <div className="blog-card__cover-overlay" />
        </Link>
      )}
      <div className="blog-card__inner">
        <div className="blog-card__meta">
          {post.category && (
            <span className="blog-card__category" style={{ color: post.category.color || 'var(--clr-gold)' }}>
              {post.category.name}
            </span>
          )}
          <span className="blog-card__date">{formatDate(post.createdAt)}</span>
        </div>

        <h2 className="blog-card__title">
          <Link to={'/blog/' + post.slug}>{post.title}</Link>
        </h2>

        <p className="blog-card__excerpt">{post.excerpt}</p>

        <div className="blog-card__footer">
          <Link to={'/blog/' + post.slug} className="blog-card__link">
            Read <FontAwesomeIcon icon={faArrowRight} />
          </Link>
          <div className="blog-card__stats">
            <span><FontAwesomeIcon icon={faClock} /> {post.readTimeMinutes}m</span>
            <span><FontAwesomeIcon icon={faEye} /> {post.viewCount || 0}</span>
            <span><FontAwesomeIcon icon={faHeart} /> {post.likeCount || 0}</span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* ── Main Blog component ──────────────────────────────────────── */
var Blog = function() {
  var postsState     = useState([]);    var posts       = postsState[0];    var setPosts       = postsState[1];
  var metaState      = useState({});    var meta        = metaState[0];     var setMeta        = metaState[1];
  var loadingState   = useState(true);  var loading     = loadingState[0];  var setLoading     = loadingState[1];
  var errorState     = useState('');    var error       = errorState[0];    var setError       = errorState[1];
  var pageState      = useState(1);     var page        = pageState[0];     var setPage        = pageState[1];
  var searchState    = useState('');    var search      = searchState[0];   var setSearch      = searchState[1];
  var sqState        = useState('');    var searchQuery = sqState[0];       var setSearchQuery = sqState[1];
  var catsState      = useState([]);    var categories  = catsState[0];     var setCategories  = catsState[1];
  var catState       = useState('');    var activeCat   = catState[0];      var setActiveCat   = catState[1];

  useEffect(function() {
    fetch(API_BASE + '/categories')
      .then(function(r) { return r.json(); })
      .then(setCategories)
      .catch(function() {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function() {
    setLoading(true); setError('');
    var params = new URLSearchParams({ page: page, limit: 9 });
    if (search)    params.set('search', search);
    if (activeCat) params.set('categoryId', activeCat);
    fetch(API_BASE + '/posts?' + params.toString())
      .then(function(r) { if (!r.ok) throw new Error('Failed to load posts'); return r.json(); })
      .then(function(data) { setPosts(data.data || []); setMeta(data.meta || {}); })
      .catch(function(e) { setError(e.message); })
      .finally(function() { setLoading(false); });
  }, [page, search, activeCat]); // eslint-disable-line react-hooks/exhaustive-deps

  var handleSearch = function(e) { e.preventDefault(); setSearch(searchQuery); setPage(1); };
  var handleCat    = function(catId) { setActiveCat(catId); setPage(1); };
  var clearSearch  = function() { setSearch(''); setSearchQuery(''); setPage(1); };

  /* Split featured from rest */
  var featured   = (!search && !activeCat && page === 1 && posts.length > 0) ? posts[0] : null;
  var restPosts  = featured ? posts.slice(1) : posts;

  return (
    <motion.div className="blog-root" initial="hidden" animate="show" variants={stagger}>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="blog-hero" aria-label="Blog header">
        <div className="blog-hero__glow" aria-hidden="true" />
        <div className="blog-hero__noise" aria-hidden="true" />

        <Container>
          <motion.div className="blog-hero__inner" variants={stagger}>

            {/* Eyebrow */}
            <motion.div className="blog-hero__eyebrow" variants={fadeUp}>
              <span className="blog-hero__tag">Technology Simplified</span>
              <span className="blog-hero__divider" aria-hidden="true" />
              <span className="blog-hero__tagline">by Jacob Chidi Eugene</span>
            </motion.div>

            {/* Headline */}
            <motion.h1 className="blog-hero__title" variants={fadeUp}>
              General Tech <em>Tips</em>
            </motion.h1>

            {/* Deck copy */}
            <motion.p className="blog-hero__sub" variants={fadeUp}>
              Practical insights on web development, software engineering, and the
              ever-evolving world of technology — written for builders, by a builder.
            </motion.p>

            {/* Search */}
            <motion.form className="blog-search" variants={fadeUp} onSubmit={handleSearch} role="search" aria-label="Search articles">
              <FontAwesomeIcon icon={faSearch} className="blog-search__icon" aria-hidden="true" />
              <input
                className="blog-search__input"
                type="search"
                placeholder="Search articles…"
                value={searchQuery}
                onChange={function(e) { setSearchQuery(e.target.value); }}
                aria-label="Search term"
              />
              {searchQuery && (
                <button type="button" className="blog-search__clear" onClick={function() { setSearchQuery(''); }} aria-label="Clear search">✕</button>
              )}
              <button type="submit" className="blog-search__btn">Search</button>
            </motion.form>

            {/* Stats strip */}
            <motion.div className="blog-hero__stats" variants={fadeUp} aria-hidden="true">
              <div className="blog-hero__stat">
                <strong>{meta.total || posts.length || '—'}</strong>
                <span>Articles</span>
              </div>
              <div className="blog-hero__stat-sep" />
              <div className="blog-hero__stat">
                <strong>{categories.length || '—'}</strong>
                <span>Topics</span>
              </div>
              <div className="blog-hero__stat-sep" />
              <div className="blog-hero__stat">
                <strong>Weekly</strong>
                <span>Updated</span>
              </div>
            </motion.div>

          </motion.div>
        </Container>
      </section>

      {/* ── Posts section ─────────────────────────────────────── */}
      <section className="blog-section" aria-labelledby="posts-heading">
        <Container>

          {/* Section header + filters */}
          <motion.div className="blog-section__top" variants={fadeUp}>
            <div className="blog-section__heading">
              <span className="home-label">
                {search ? 'Search Results' : activeCat ? 'Filtered' : 'Latest Articles'}
              </span>
              <h2 id="posts-heading" className="blog-section__title">
                {search
                  ? <><em>"{search}"</em></>
                  : activeCat
                    ? categories.find(function(c) { return c.id === activeCat; })?.name || 'Articles'
                    : 'All Posts'}
              </h2>
            </div>

            {/* Category pills */}
            {categories.length > 0 && (
              <div className="blog-filters" role="group" aria-label="Filter by category">
                <button
                  className={'blog-filter-btn' + (!activeCat ? ' blog-filter-btn--active' : '')}
                  onClick={function() { handleCat(''); }}
                >
                  All
                </button>
                {categories.map(function(cat) {
                  var isActive = activeCat === cat.id;
                  return (
                    <button
                      key={cat.id}
                      className={'blog-filter-btn' + (isActive ? ' blog-filter-btn--active' : '')}
                      style={isActive && cat.color ? { '--filter-color': cat.color, borderColor: cat.color, color: cat.color } : {}}
                      onClick={function() { handleCat(cat.id); }}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* ── Loading ─────────────────────────────────────────── */}
          <AnimatePresence>
            {loading && (
              <motion.div className="blog-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading articles…</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Error ───────────────────────────────────────────── */}
          {!loading && error && (
            <motion.div className="blog-error" variants={fadeUp}>
              <p>Could not load posts. Make sure the backend is running.</p>
              <code>{error}</code>
            </motion.div>
          )}

          {/* ── Empty ───────────────────────────────────────────── */}
          {!loading && !error && posts.length === 0 && (
            <motion.div className="blog-empty" variants={fadeUp}>
              <span className="blog-empty__icon" aria-hidden="true">✦</span>
              <p className="blog-empty__title">No articles found</p>
              <p className="blog-empty__sub">
                {search ? 'Try a different search term.' : 'Check back soon — articles are being added.'}
              </p>
              {search && (
                <button className="home-btn home-btn--outline" onClick={clearSearch} style={{ marginTop: 20 }}>
                  Clear search
                </button>
              )}
            </motion.div>
          )}

          {/* ── Featured post ────────────────────────────────────── */}
          {!loading && !error && featured && (
            <FeaturedPost post={featured} index={0} />
          )}

          {/* ── Post grid ────────────────────────────────────────── */}
          {!loading && !error && restPosts.length > 0 && (
            <motion.div className="blog-grid" variants={cardStagger} role="list">
              {restPosts.map(function(post, i) {
                return <PostCard key={post.id} post={post} index={i} />;
              })}
            </motion.div>
          )}

          {/* ── Pagination ───────────────────────────────────────── */}
          {meta.totalPages > 1 && (
            <motion.div className="blog-pagination" variants={fadeUp}>
              <button
                className="blog-page-btn"
                disabled={page <= 1}
                onClick={function() { setPage(function(p) { return p - 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                ← Previous
              </button>
              <div className="blog-pagination__pages">
                {Array.from({ length: meta.totalPages }, function(_, i) { return i + 1; }).map(function(p) {
                  return (
                    <button
                      key={p}
                      className={'blog-page-num' + (p === page ? ' blog-page-num--active' : '')}
                      onClick={function() { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      aria-current={p === page ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <button
                className="blog-page-btn"
                disabled={page >= meta.totalPages}
                onClick={function() { setPage(function(p) { return p + 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >
                Next →
              </button>
            </motion.div>
          )}

        </Container>
      </section>
    </motion.div>
  );
};

export default Blog;